import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import {
  Calendar, Clock, Video, Ticket, ExternalLink, 
  Loader2, CheckCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MySeminars() {
  const { i18n } = useTranslation();
  const isEl = i18n.language === 'el';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingZoomLink, setLoadingZoomLink] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/seminars/my/tickets`, { headers });
      setTickets(res.data);
    } catch (e) {
      console.error('Failed to load tickets:', e);
      toast.error(e.response?.status === 404
        ? (isEl ? 'Τα εισιτήρια σεμιναρίων δεν είναι ακόμη διαθέσιμα.' : 'Seminar tickets endpoint is not available yet.')
        : (e.response?.data?.detail || (isEl ? 'Σφάλμα φόρτωσης εισιτηρίων.' : 'Failed to load tickets.')));
    } finally {
      setLoading(false);
    }
  };

  const getZoomLink = async (ticketId) => {
    setLoadingZoomLink(ticketId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/seminars/my/tickets/${ticketId}/zoom-link`, { headers });
      
      if (res.data.join_url) {
        window.open(res.data.join_url, '_blank');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zoom link not available');
    } finally {
      setLoadingZoomLink(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'EEEE, dd MMMM yyyy', { locale: i18n.language === 'el' ? el : enUS });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'HH:mm', { locale: i18n.language === 'el' ? el : enUS });
    } catch {
      return dateStr;
    }
  };

  const isUpcoming = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) > new Date();
  };

  const isLive = (startStr, durationMinutes) => {
    if (!startStr) return false;
    const start = new Date(startStr);
    const end = new Date(start.getTime() + (durationMinutes || 60) * 60 * 1000);
    const now = new Date();
    return now >= start && now <= end;
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-misel-dark">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <Ticket className="mx-auto mb-4 text-gray-500" size={48} />
          <h2 className="text-2xl font-bold text-white mb-4">
            {i18n.language === 'el' ? 'Τα Σεμινάριά μου' : 'My Seminars'}
          </h2>
          <p className="text-gray-400 mb-6">
            {i18n.language === 'el' 
              ? 'Συνδεθείτε για να δείτε τα σεμινάριά σας' 
              : 'Log in to view your seminars'}
          </p>
          <Link to="/login">
            <Button className="bg-primary hover:bg-primary/90">
              {i18n.language === 'el' ? 'Σύνδεση' : 'Login'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-misel-dark">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Ticket className="text-primary" size={28} />
          <h1 className="text-3xl font-bold text-white">
            {i18n.language === 'el' ? 'Τα Σεμινάριά μου' : 'My Seminars'}
          </h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-16">
            <Ticket className="mx-auto mb-4 text-gray-500" size={48} />
            <p className="text-gray-400 mb-6">
              {i18n.language === 'el' 
                ? 'Δεν έχετε εγγραφεί σε κάποιο σεμινάριο ακόμα' 
                : "You haven't signed up for any seminars yet"}
            </p>
            <Link to="/seminars">
              <Button className="bg-primary hover:bg-primary/90">
                {i18n.language === 'el' ? 'Δες τα Σεμινάρια' : 'Browse Seminars'}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-6">
            {tickets.map((ticket) => {
              const seminar = ticket.seminar;
              const upcoming = isUpcoming(seminar?.start_at);
              const live = isLive(seminar?.start_at, seminar?.duration_minutes);
              
              return (
                <div 
                  key={ticket.id} 
                  className={`bg-misel-card border rounded-sm overflow-hidden ${
                    live ? 'border-green-500/50' : 'border-white/10'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex-1">
                        {/* Status badges */}
                        <div className="flex items-center gap-2 mb-3">
                          {live && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 animate-pulse flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-green-400"></span>
                              LIVE NOW
                            </span>
                          )}
                          {!live && upcoming && (
                            <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                              {i18n.language === 'el' ? 'Προσεχώς' : 'Upcoming'}
                            </span>
                          )}
                          {!upcoming && !live && (
                            <span className="px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400">
                              {i18n.language === 'el' ? 'Ολοκληρωμένο' : 'Completed'}
                            </span>
                          )}
                          <span className="px-2 py-1 rounded text-xs bg-primary/20 text-primary flex items-center gap-1">
                            <CheckCircle size={12} />
                            {i18n.language === 'el' ? 'Εγγεγραμμένος' : 'Registered'}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {i18n.language === 'el' ? seminar?.title_el : seminar?.title_en || seminar?.title_el}
                        </h3>

                        {/* Date & Time */}
                        <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDate(seminar?.start_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {formatTime(seminar?.start_at)} ({seminar?.duration_minutes} min)
                          </span>
                        </div>

                        {/* Instructor */}
                        {seminar?.instructor && (
                          <p className="text-sm text-gray-500">
                            {i18n.language === 'el' ? 'Εκπαιδευτής' : 'Instructor'}: {seminar.instructor}
                          </p>
                        )}
                      </div>

                      {/* Join button */}
                      <div className="flex flex-col gap-2">
                        {(upcoming || live) && ticket.has_zoom_link && (
                          <Button
                            onClick={() => getZoomLink(ticket.id)}
                            className={`${
                              live 
                                ? 'bg-green-600 hover:bg-green-700' 
                                : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                            disabled={loadingZoomLink === ticket.id}
                          >
                            {loadingZoomLink === ticket.id ? (
                              <Loader2 size={16} className="animate-spin mr-2" />
                            ) : (
                              <Video size={16} className="mr-2" />
                            )}
                            {live 
                              ? (i18n.language === 'el' ? 'Μπες Τώρα' : 'Join Now')
                              : (i18n.language === 'el' ? 'Zoom Link' : 'Zoom Link')
                            }
                            <ExternalLink size={14} className="ml-1" />
                          </Button>
                        )}
                        
                        {!ticket.has_zoom_link && upcoming && (
                          <div className="flex items-center gap-2 text-sm text-yellow-400">
                            <AlertCircle size={14} />
                            {i18n.language === 'el' 
                              ? 'Zoom link σύντομα διαθέσιμο' 
                              : 'Zoom link coming soon'}
                          </div>
                        )}

                        {/* Ticket ID */}
                        <p className="text-xs text-gray-600">
                          Ticket: {ticket.id.slice(0, 8)}...
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Link to all seminars */}
        <div className="mt-8 text-center">
          <Link to="/seminars" className="text-primary hover:underline">
            {i18n.language === 'el' ? 'Δες όλα τα διαθέσιμα σεμινάρια →' : 'See all available seminars →'}
          </Link>
        </div>
      </div>
    </div>
  );
}
